"use client";

import {
  Button,
  cn,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import {
  ChevronRight,
  PackageIcon,
  ScanBarcodeIcon,
} from "lucide-react";
import type { RefObject } from "react";
import { formatInvoiceCurrencyOptionHint } from "@/modules/merchant/presentation/lib/pos-fx-labels";
import type { ShopPosCartLine } from "@/modules/merchant/presentation/ui/ShopPosCheckoutModal";
import {
  formatPrice,
  isShopCurrency,
  SHOP_CURRENCIES,
  type ShopCurrency,
} from "@/shared/lib/currency";
import { resolveImageSrc } from "@/shared/ui/AppImage";

const POS_SCAN_MAX_LEN = 100;

type LastScannedLine = ShopPosCartLine & {
  lotSummary?: string;
  serialLabel?: string;
};

export type ShopPosScanAsideProps = {
  simpleMode: boolean;
  barcodeInputRef: RefObject<HTMLInputElement | null>;
  barcodeFieldId: string;
  barcode: string;
  setBarcode: (value: string) => void;
  debouncedAutoScan: (value: string) => void;
  runScanForCode: (code: string) => void | Promise<void>;
  scanPending: boolean;
  posSalesUnlocked: boolean;
  lastScannedLine: LastScannedLine | null;
  cartSubtotalFinite: boolean;
  cartSubtotal: number;
  invoiceCurrency: ShopCurrency;
  hasMixedCurrencyCart: boolean;
  setInvoiceCurrency: (currency: ShopCurrency) => void;
  cartFxSourceCurrencies: string[];
  rateMap: ReadonlyMap<string, number>;
  exchangeRatesAsOf: string | null | undefined;
  exchangeRatesLoading: boolean;
  shopId: string | undefined;
  billDiscountUsePercent: boolean;
  setBillDiscountUsePercent: (value: boolean) => void;
  billDiscountPercent: number;
  setBillDiscountPercent: (value: number) => void;
  billDiscount: number;
  setBillDiscount: (value: number) => void;
  focusBarcodeInput: () => void;
  cartLength: number;
  branchId: string | null;
  canSell: boolean;
  onCheckout: () => void;
  onClearCart: () => void;
  className?: string;
};

export function ShopPosScanAside({
  simpleMode,
  barcodeInputRef,
  barcodeFieldId,
  barcode,
  setBarcode,
  debouncedAutoScan,
  runScanForCode,
  scanPending,
  posSalesUnlocked,
  lastScannedLine,
  cartSubtotalFinite,
  cartSubtotal,
  invoiceCurrency,
  hasMixedCurrencyCart,
  setInvoiceCurrency,
  cartFxSourceCurrencies,
  rateMap,
  exchangeRatesAsOf,
  exchangeRatesLoading,
  shopId,
  billDiscountUsePercent,
  setBillDiscountUsePercent,
  billDiscountPercent,
  setBillDiscountPercent,
  billDiscount,
  setBillDiscount,
  focusBarcodeInput,
  cartLength,
  branchId,
  canSell,
  onCheckout,
  onClearCart,
  className,
}: ShopPosScanAsideProps) {
  const Wrapper = simpleMode ? "div" : "aside";

  return (
    <Wrapper className={cn("w-full shrink-0 space-y-4", className)}>
      <div className="rounded-xl border-2 border-primary/25 bg-card p-4 shadow-sm">
        <h2 className="mb-1 font-bold font-heading text-card-foreground text-xs uppercase tracking-wide">
          {simpleMode ? "ສະແກນ" : "ສະແກນ barcode ຮ້ານ"}
        </h2>
        {simpleMode ? (
          <p className="mb-3 text-[11px] text-muted-foreground leading-snug">
            ສະແກນ ຫຼື ພິມ barcode ແລ້ວກົດ Enter — lot/serial ຈະໃຫ້ເລືອກເພີ່ມ
          </p>
        ) : (
          <p className="mb-3 text-[11px] text-muted-foreground leading-snug">
            ຫຼັງສະແກນ ~90ms ລະບົບຈະຄົ້ນ barcode ສິນຄ້າແລ້ວເພີ່ມໃສ່ກະຕ່າ (
            <span className="font-medium text-foreground">ສິນຄ້າ lot/serial</span>{" "}
            ຈະເປີດໃຫ້ເລືອກ lot ຫຼື serial). ຖ້າບໍ່ພົບສິນຄ້າ ຈະລອງຄົ້ນ{" "}
            <span className="font-medium text-foreground">serial</span> ຕາມສາຂາ.
            ລະຫັດຍາວສູງສຸດ {POS_SCAN_MAX_LEN} ຕົວ. ກົດ{" "}
            <span className="font-medium text-foreground">Enter</span> ກວດຄືນໄດ້.
          </p>
        )}
        <div className="relative">
          <ScanBarcodeIcon
            className="pointer-events-none absolute top-1/2 left-3 size-5 -translate-y-1/2 text-primary"
            aria-hidden
          />
          <Input
            ref={barcodeInputRef}
            id={barcodeFieldId}
            value={barcode}
            onChange={(e) => {
              const v = e.target.value;
              setBarcode(v);
              debouncedAutoScan(v);
            }}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              e.preventDefault();
              void runScanForCode(e.currentTarget.value);
            }}
            placeholder="ລໍຖ້າສະແກນ..."
            className="h-11 border-border pr-3 pl-10 font-mono text-sm"
            autoComplete="off"
            disabled={scanPending || !posSalesUnlocked}
            autoFocus
          />
        </div>
        {lastScannedLine ? (
          <div className="mt-3 rounded-lg border border-secondary/40 bg-secondary/10 p-3">
            <p className="font-semibold text-[10px] text-secondary uppercase tracking-wide">
              ສະແກນລ່າສຸດ
            </p>
            <div className="mt-2 flex gap-3">
              <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-background">
                {(() => {
                  const src = lastScannedLine.row.imageUrl
                    ? resolveImageSrc(lastScannedLine.row.imageUrl)
                    : null;
                  return src ? (
                    <img
                      src={src}
                      alt=""
                      className="size-full object-cover"
                    />
                  ) : (
                    <PackageIcon className="size-6 text-secondary" />
                  );
                })()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-bold font-heading text-card-foreground text-sm leading-tight">
                  {lastScannedLine.row.productName}
                </p>
                <p className="mt-0.5 font-mono text-muted-foreground text-xs">
                  SKU: {lastScannedLine.row.shopSku}
                </p>
                {lastScannedLine.row.categoryName ? (
                  <p className="mt-1 font-medium text-secondary text-xs">
                    ໝວດ: {lastScannedLine.row.categoryName}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <div
        className={cn(
          "rounded-xl border border-border bg-card p-4 shadow-sm",
          simpleMode && "hidden lg:block",
        )}
      >
        <div className="space-y-2 text-sm">
          <div className="border-border border-b pb-3">
            <p className="text-muted-foreground text-xs uppercase tracking-wide">
              ລວມທັງໝົດ
            </p>
            <p className="font-bold font-heading text-3xl text-card-foreground tabular-nums tracking-tight">
              {cartSubtotalFinite
                ? formatPrice(cartSubtotal, invoiceCurrency)
                : "—"}
            </p>
            <p className="mt-0.5 text-muted-foreground text-xs">
              {invoiceCurrency}{" "}
              <span className="text-[10px]">
                (ຫຼັງຫຼຸດລາຍການ — ຫຼຸດບິນຕອນຊຳລະ)
              </span>
            </p>
            {hasMixedCurrencyCart ? (
              <div className="mt-2 space-y-1.5">
                <Label
                  htmlFor="pos-invoice-currency"
                  className="text-card-foreground text-xs"
                >
                  ສະກຸນບິນ
                </Label>
                <Select
                  value={invoiceCurrency}
                  onValueChange={(v) => {
                    if (isShopCurrency(v)) setInvoiceCurrency(v);
                  }}
                >
                  <SelectTrigger
                    id="pos-invoice-currency"
                    className="h-9 w-full font-mono text-sm"
                  >
                    <SelectValue placeholder={invoiceCurrency} />
                  </SelectTrigger>
                  <SelectContent>
                    {SHOP_CURRENCIES.map((c) => {
                      const hint = formatInvoiceCurrencyOptionHint(
                        c,
                        cartFxSourceCurrencies,
                        rateMap,
                      );
                      const sub =
                        hint !== ""
                          ? hint
                          : cartFxSourceCurrencies.includes(c)
                            ? "ກົງກັບລາຍການ"
                            : "—";
                      return (
                        <SelectItem key={c} value={c}>
                          <span className="flex min-w-0 max-w-[min(92vw,22rem)] items-baseline justify-between gap-3">
                            <span className="shrink-0 font-medium font-mono">
                              {c}
                            </span>
                            <span className="truncate font-normal text-[11px] text-muted-foreground">
                              {sub}
                            </span>
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {exchangeRatesAsOf ? (
                  <p className="text-[10px] text-muted-foreground">
                    ອັດຕາອ້າງອີງ:{" "}
                    {new Date(exchangeRatesAsOf).toLocaleString("lo-LA")}
                  </p>
                ) : null}
                {exchangeRatesLoading ? (
                  <p className="text-muted-foreground text-xs">ກຳລັງໂຫຼດອັດຕາ…</p>
                ) : null}
                {!cartSubtotalFinite && !exchangeRatesLoading ? (
                  <p className="text-amber-700 text-xs dark:text-amber-300">
                    ຕ້ອງມີອັດຕາແລກຄູ່ສະກຸນ —{" "}
                    <Link
                      to="/app/shops/$shopId/settings"
                      params={{ shopId: shopId ?? "" }}
                      className="font-medium text-primary underline underline-offset-2"
                    >
                      ຕັ້ງຄ່າຮ້ານ
                    </Link>{" "}
                    (ອັດຕາແລກ)
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
          {!simpleMode ? (
            <div className="mt-3">
              <label
                htmlFor="pos-sidebar-bill-disc"
                className="mb-1 block font-medium text-card-foreground text-xs"
              >
                ສ່ວນຫຼຸດທັງບິນ (ພັບ / ຊຳລະ)
              </label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={!billDiscountUsePercent ? "default" : "outline"}
                  className="h-9 px-3 text-xs"
                  onClick={() => {
                    setBillDiscountUsePercent(false);
                    setBillDiscountPercent(0);
                  }}
                >
                  ເງິນ
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={billDiscountUsePercent ? "default" : "outline"}
                  className="h-9 px-3 text-xs"
                  onClick={() => {
                    setBillDiscountUsePercent(true);
                    const next =
                      billDiscount > 0 &&
                      cartSubtotalFinite &&
                      cartSubtotal > 0
                        ? Math.min((billDiscount / cartSubtotal) * 100, 100)
                        : 0;
                    setBillDiscountPercent(next);
                  }}
                >
                  %
                </Button>
                {billDiscountUsePercent ? (
                  <Input
                    id="pos-sidebar-bill-disc"
                    type="number"
                    min={0}
                    max={100}
                    step={0.01}
                    className="h-9 w-24 font-mono text-sm"
                    value={billDiscountPercent > 0 ? billDiscountPercent : ""}
                    placeholder="0"
                    onChange={(e) => {
                      const raw = e.target.value;
                      const n = raw === "" ? 0 : Number.parseFloat(raw);
                      const v = Number.isFinite(n)
                        ? Math.min(Math.max(0, n), 100)
                        : 0;
                      setBillDiscountPercent(v);
                    }}
                  />
                ) : (
                  <Input
                    id="pos-sidebar-bill-disc"
                    type="number"
                    min={0}
                    step={1}
                    className="h-9 font-mono text-sm"
                    value={billDiscount > 0 ? billDiscount : ""}
                    placeholder="0"
                    onChange={(e) => {
                      const raw = e.target.value;
                      const n = raw === "" ? 0 : Number.parseFloat(raw);
                      const cap = cartSubtotalFinite ? cartSubtotal : 0;
                      setBillDiscount(
                        Number.isFinite(n) && n >= 0 ? Math.min(n, cap) : 0,
                      );
                    }}
                  />
                )}
              </div>
            </div>
          ) : null}
        </div>

        {simpleMode ? (
          <div className="mt-4 hidden grid-cols-3 gap-2 lg:grid">
            <Button
              type="button"
              className="h-14 flex-col gap-1 bg-emerald-600 font-bold text-white hover:bg-emerald-700"
              disabled={!posSalesUnlocked}
              onClick={focusBarcodeInput}
            >
              <ScanBarcodeIcon className="size-5" aria-hidden />
              ສະແກນ
            </Button>
            <Button
              type="button"
              className="h-14 flex-col gap-1 bg-sky-600 font-bold text-white hover:bg-sky-700"
              disabled={
                cartLength === 0 ||
                !branchId ||
                !canSell ||
                !posSalesUnlocked ||
                !cartSubtotalFinite
              }
              onClick={onCheckout}
            >
              <ChevronRight className="size-5" aria-hidden />
              ຊຳລະ
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="h-14 flex-col gap-1 font-bold"
              disabled={cartLength === 0}
              onClick={onClearCart}
            >
              ຍົກເລີກ
            </Button>
          </div>
        ) : null}

        <div className="mt-4 space-y-2">
          {!simpleMode ? (
            <>
              <Button
                type="button"
                className="h-11 w-full gap-2 font-semibold"
                disabled={
                  cartLength === 0 ||
                  !branchId ||
                  !canSell ||
                  !posSalesUnlocked ||
                  !cartSubtotalFinite
                }
                title={
                  !branchId
                    ? "ຕ້ອງມີສາຂາ"
                    : !canSell
                      ? "ຕ້ອງມີສິດຂາຍ"
                      : !posSalesUnlocked
                        ? "ຕ້ອງເປີດກະກ່ອນ"
                        : !cartSubtotalFinite
                          ? "ກວດອັດຕາແລກ / ສະກຸນບິນ"
                          : undefined
                }
                onClick={onCheckout}
              >
                ດຳເນີນການຊຳລະ
                <ChevronRight className="size-4" />
              </Button>
              <button
                type="button"
                className="w-full pt-1 text-center font-medium text-destructive text-sm underline-offset-4 hover:underline"
                onClick={onClearCart}
                disabled={cartLength === 0}
              >
                ລ້າງກະຕ່າ
              </button>
            </>
          ) : null}
        </div>
      </div>
    </Wrapper>
  );
}
